/**
 * @file Route guard: requires session (and optional admin/manage/feature/app-access).
 * Unauthenticated users soft-redirect to login with a same-origin returnUrl.
 * @module frontend/src/components/ProtectedRoute
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { APP_HOME } from '../constants';
import { Loader } from 'lucide-react';

/**
 * Allow only relative same-origin paths for post-login return (blocks //evil).
 * @param {string} pathname
 * @param {string} [search]
 * @returns {string}
 */
function safeReturnUrl(pathname, search) {
  const full = `${pathname}${search || ''}`;
  // REVIEW [LOW]: Weaker than Login/Register (no `://` check) — safe for router pathname+search,
  // but do not reuse this helper for raw query-string returnUrl values.
  if (!full.startsWith('/') || full.startsWith('//')) return APP_HOME;
  return full;
}

const GRACE_ALLOWED_PREFIXES = ['/account', '/company', '/complete-company'];

/**
 * Wraps children that require authentication and optional capability checks.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.requirePlatformAdmin]
 * @param {boolean} [props.requireManageUsers]
 * @param {string|null} [props.requireFeature]
 * @param {boolean} [props.requireAppAccess]
 */
export default function ProtectedRoute({
  children,
  requirePlatformAdmin = false,
  requireManageUsers = false,
  requireFeature = null,
  requireAppAccess = false,
}) {
  const { user, loading, isPlatformAdmin, canManageUsers, canUseApp, company, features } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="container" style={{ padding: '40px', textAlign: 'center' }}>
        <Loader className="spin" size={32} />
        <p>Checking session...</p>
      </div>
    );
  }

  if (!user) {
    const returnUrl = safeReturnUrl(location.pathname, location.search);
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  if (!company && !isPlatformAdmin && location.pathname !== '/complete-company') {
    return (
      <Navigate
        to={`/complete-company?returnUrl=${encodeURIComponent(safeReturnUrl(location.pathname, location.search))}`}
        replace
      />
    );
  }

  if (requirePlatformAdmin && !isPlatformAdmin) {
    return <Navigate to={APP_HOME} replace />;
  }

  if (requireManageUsers && !canManageUsers) {
    return <Navigate to={APP_HOME} replace />;
  }

  if (requireFeature && !features?.[requireFeature]) {
    return <Navigate to={APP_HOME} replace />;
  }

  const path = location.pathname;
  const graceAllowed =
    GRACE_ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`)) ||
    path.startsWith('/admin');

  if (company && !canUseApp && !isPlatformAdmin && !graceAllowed) {
    const dest = canManageUsers ? '/company' : '/account';
    return <Navigate to={dest} replace />;
  }

  if (requireAppAccess && !canUseApp && !isPlatformAdmin) {
    const dest = canManageUsers ? '/company' : '/account';
    return <Navigate to={dest} replace />;
  }

  return children;
}
